/**
 * @lines98/client/lib/session-heartbeat — liveness-пинг отозванной сессии на уровне shell.
 *
 * ЧТО: одна shell-подписка на возврат вкладки/окна в фокус, которая для
 *   АВТОРИЗОВАННОЙ identity делает дешёвый авторизованный GET — чтобы
 *   «прощупать» отозванный токен и поднять плашку «Сессия завершена», даже если
 *   текущий экран сам не шлёт авторизованных запросов.
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-bug 2026-06-11, интермиттентно): вход в аккаунт
 *   с ДРУГОГО браузера ротирует `players.secret_token_hash` → токен этого
 *   браузера мёртв. Но отзыв детектится ТОЛЬКО когда летит АВТОРИЗОВАННЫЙ запрос
 *   и упирается в 401 `token_not_recognized` (api-client → emitSessionRevoked →
 *   плашка). На доске дня хендлеры фокуса/видимости дёргают лишь ПУБЛИЧНЫЕ
 *   запросы (`getDailySeed`/rollover-resync — без Bearer → не 401) → отзыв не
 *   щупается → доска просто сереет/пустеет БЕЗ плашки. Таймингозависимо («иногда
 *   норм, иногда так»). Этот heartbeat screen-agnostic: на возврат фокуса он сам
 *   шлёт авторизованный liveness-GET, гарантируя, что мёртвый токен будет
 *   замечен и плашка всплывёт независимо от того, какой экран открыт.
 *
 * КАК: пинг — `getMetaState()` (`GET /meta/state`). Это REQUIRED-auth эндпоинт
 *   (server `app.authenticate` preHandler — meta-state.ts §«required-auth»): на
 *   отозванном Bearer сервер хеширует токен, не находит строку и отвечает 401
 *   `{ reason: 'token_not_recognized' }` (auth.ts step 4). api-client сам видит
 *   этот 401 на авторизованной identity → `emitSessionRevoked()`. РЕЗУЛЬТАТ пинга
 *   нам не нужен — только сам факт авторизованного запроса (никакого UI-side-
 *   effect: НЕ переиспользуем HUD-refresh, чтобы не дёргать экраны). Best-effort:
 *   offline/любая ошибка проглатывается — единственный значимый исход (401→revoke)
 *   публикует api-client сам.
 *
 * SCOPE: только клиентский UX-сигнал над уже-существующим серверным 401 — щупает
 *   мёртвый токен раньше. Серверную auth / анти-чит НЕ трогает (incident
 *   2026-06-10 — тихий kick плодил призрачного анонима). НЕ лечит «серую доску»
 *   отдельно: надёжное всплытие плашки (она перезагружает на чистый старт по
 *   выбору игрока) и есть фикс.
 * NOT IN SCOPE: focus-хендлеры экранов (leaderboard focus-refresh, daily
 *   rollover-resync) — это ОТДЕЛЬНЫЕ подписки экранов, heartbeat их не трогает и
 *   не энтанглит.
 *
 * Гарды:
 *   - аноним/гость (НЕ авторизован) → пропуск: только авторизованные получают
 *     multi-browser revoke; гостю re-mint — норма, не отзыв.
 *   - revoked уже pending (плашка поднята) → пропуск: не спамим, эпизод обработан.
 *   - троттл (min-interval-gate) → серия focus/visibility-событий (alt-tab
 *     дёрганье, оконные менеджеры) не бьёт по сети чаще раза в окно.
 *
 * Cross-references:
 *   - services/meta-api.ts (getMetaState — авторизованный liveness-пинг).
 *   - services/identity.ts (isAuthed — авторизован ли игрок).
 *   - services/api-client.ts (revoked-detect: 401 token_not_recognized → emit).
 *   - lib/session-revoked.ts (isSessionRevokedPending — анти-дубль гард).
 *   - lib/min-interval-gate.ts (троттл — переиспользуемая «ручка»).
 *   - app.ts bootShell (installs once рядом с installSessionRevokedBanner).
 */

import { isAuthed } from '../services/identity.js';
import { getMetaState } from '../services/meta-api.js';

import { createMinIntervalGate } from './min-interval-gate.js';
import { isSessionRevokedPending } from './session-revoked.js';

/**
 * Минимальный интервал между liveness-пингами по возврату фокуса/вкладки. Серия
 * focus/visibilitychange-событий (alt-tab дёрганье, оконные менеджеры шлют пачку)
 * НЕ должна спамить сеть. Это ТАЙМИНГ-константа поведения клиента (не CSS-размер,
 * не игровое число) → именованная, не magic-число (code-structure §4). ~5 с —
 * достаточно часто, чтобы отзыв всплыл почти сразу при возврате во вкладку, но без
 * флуда при дёрганом фокусе. Сонастроено с leaderboard FOCUS_REFRESH (~10 с): тут
 * порог короче, т.к. цель — БЫСТРО заметить мёртвый токен, а пинг дешёвый и
 * выполняется лишь для авторизованных.
 */
const HEARTBEAT_MIN_INTERVAL_MS = 5_000;

/**
 * Завести liveness-heartbeat на уровне shell. Идемпотентно по семантике вызова:
 * app-shell зовёт ОДИН раз на boot (рядом с installSessionRevokedBanner).
 *
 * @returns teardown — снимает слушатели (симметрия с другими shell-подписками).
 */
export function installSessionHeartbeat(): () => void {
  // Per-install троттл-гейт (своя «последняя точка пуска»).
  const gate = createMinIntervalGate(HEARTBEAT_MIN_INTERVAL_MS);

  const ping = (): void => {
    // Гард 1: только авторизованные получают multi-browser revoke. Аноним/гость
    // → re-mint это норма (fresh-browser поведение), не отзыв — пинговать незачем.
    if (!isAuthed()) return;
    // Гард 2: плашка уже поднята (необработанный revoked-эпизод) — не плодим
    // лишний пинг/эмит, эпизод обрабатывается игроком.
    if (isSessionRevokedPending()) return;
    // Гард 3: троттл — не чаще раза в окно (анти-спам серии focus/visibility).
    if (!gate.allow()) return;
    // Liveness-GET: авторизованный (api-client сам цепляет Bearer). Отозванный
    // токен → 401 token_not_recognized → emitSessionRevoked → плашка. Результат
    // ИГНОРИРУЕМ (нужен лишь сам запрос). `getMetaState` сам глотает offline/
    // ошибки в `{ ok:false }`-ветку (никогда не reject) — best-effort, значимый
    // исход (401→revoke) публикует api-client. `.catch` — на случай будущего
    // throw из api-client, чтобы liveness-пинг не ронял unhandled-rejection.
    void getMetaState().catch(() => {
      /* swallow — liveness best-effort */
    });
  };

  // visibilitychange срабатывает и на скрытие — пингуем только когда вкладка СНОВА
  // видима (возврат во вкладку), как в leaderboard/daily focus-хендлерах.
  const onVisible = (): void => {
    if (document.visibilityState === 'visible') ping();
  };
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', ping);

  return () => {
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('focus', ping);
  };
}
